import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreVertical, ArrowRight, Trash2, Phone, Calendar, AlertCircle, Building2, MessageCircle } from "lucide-react";
import { useDragKanban } from "@/hooks/useDragKanban";

const STAGES = [
  { id: "identificado", label: "Identificado", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { id: "contato_feito", label: "Contato feito", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { id: "conversa_iniciada", label: "Conversa iniciada", color: "bg-purple-100 text-purple-700 border-purple-200" },
  { id: "visita_captacao", label: "Visita de captação", color: "bg-orange-100 text-orange-700 border-orange-200" },
  { id: "agenciado", label: "Agenciado", color: "bg-green-100 text-green-700 border-green-200" },
];

const EMPTY = { title: "", address: "", phone: "", listing_type: "venda", agency_name: "", notes: "", photo_url: "" };

const COPY_TEMPLATE = "Olá, tudo bem? Vi o anúncio do seu imóvel à venda e tenho interesse em conhecer melhor para apresentar a compradores qualificados da minha carteira. Posso te ligar agora ou prefere conversar por aqui?";

export default function Agenciamento() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agenciamentos");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setDialogOpen(true); };
  const openEdit = (it) => { setForm({ ...EMPTY, ...it }); setEditingId(it.id); setDialogOpen(true); };

  const save = async () => {
    if (!form.title?.trim() && !form.address?.trim()) { toast.error("Coloca pelo menos endereço ou título"); return; }
    if (!form.phone?.trim()) { toast.error("Telefone do proprietário é obrigatório"); return; }
    try {
      if (editingId) await api.patch(`/agenciamentos/${editingId}`, form);
      else await api.post("/agenciamentos", form);
      toast.success("Salvo!");
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const moveStage = async (item, newStage) => {
    await api.patch(`/agenciamentos/${item.id}`, { stage: newStage });
    if (newStage === "agenciado") {
      toast.success("Boa, mais um na sua pauta! Vou cruzar com seus leads.");
    } else {
      toast.success(`Movido para ${STAGES.find(s => s.id === newStage)?.label}`);
    }
    load();
  };

  const remove = async (id) => {
    if (!confirm("Excluir esse agenciamento?")) return;
    await api.delete(`/agenciamentos/${id}`);
    toast.success("Excluído"); load();
  };

  const buildWaLink = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");
    const p = digits.startsWith("55") ? digits : `55${digits}`;
    return `https://wa.me/${p}?text=${encodeURIComponent(COPY_TEMPLATE)}`;
  };

  const byStage = (s) => items.filter((i) => i.stage === s);

  const dnd = useDragKanban(async (itemId, fromStage, toStage) => {
    await api.patch(`/agenciamentos/${itemId}`, { stage: toStage });
    if (toStage === "agenciado") toast.success("Boa, mais um na sua pauta! Cruzando com seus leads.");
    else toast.success(`Movido para ${STAGES.find(s => s.id === toStage)?.label}`);
    load();
  });

  return (
    <div className="p-5 lg:p-10 max-w-7xl mx-auto" data-testid="agenciamento-root">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4651A] font-bold mb-1">Captação</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C19] flex items-center gap-2">
            <Building2 className="w-7 h-7 text-[#1B3A2F]" /> Agenciamento
          </h1>
          <p className="text-stone-500 mt-1 text-sm">{items.length} no funil. Mande foto de placa pelo WhatsApp pra registrar automático.</p>
        </div>
        <Button data-testid="add-agenciamento-btn" onClick={openCreate} className="bg-[#1B3A2F] hover:bg-[#244A3C] text-white">
          <Plus className="w-4 h-4 mr-2" /> Novo agenciamento
        </Button>
      </div>

      {loading ? (
        <div className="text-stone-500">Carregando…</div>
      ) : (
        <div className="flex gap-4 overflow-x-auto kanban-scroll pb-4">
          {STAGES.map((s) => {
            const list = byStage(s.id);
            return (
              <div
                key={s.id}
                className={`flex-shrink-0 w-72 rounded-lg transition ${dnd.overStage === s.id ? 'bg-[#1B3A2F]/8 ring-2 ring-[#1B3A2F]/30' : ''}`}
                data-testid={`ag-column-${s.id}`}
                {...dnd.column(s.id)}
              >
                <div className={`px-3 py-2 rounded-lg border ${s.color} flex items-center justify-between mb-3`}>
                  <div className="font-semibold text-sm">{s.label}</div>
                  <Badge variant="secondary" className="bg-white/70">{list.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[60px]">
                  {list.length === 0 ? (
                    <div className="text-xs text-stone-400 text-center py-6 border border-dashed border-stone-200 rounded-lg">
                      Arrasta um card aqui
                    </div>
                  ) : list.map((it) => (
                    <div key={it.id} {...dnd.card(it.id, s.id)}>
                      <AgCard item={it}
                        onClick={() => openEdit(it)} onMove={moveStage} onDelete={remove}
                        waLink={buildWaLink(it.phone)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar agenciamento" : "Novo agenciamento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Endereço ou descrição">
              <Input data-testid="ag-title-input" value={form.title || ""} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Casa da Trindade" />
            </Field>
            <Field label="Endereço completo (se souber)">
              <Input value={form.address || ""} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="R. Lauro Linhares, 123" />
            </Field>
            <Field label="Telefone do proprietário *">
              <Input data-testid="ag-phone-input" value={form.phone || ""} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="48999999999" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo">
                <Select value={form.listing_type} onValueChange={(v) => setForm({...form, listing_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="venda">Venda</SelectItem>
                    <SelectItem value="aluguel">Aluguel</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Imobiliária (se houver)">
                <Input value={form.agency_name || ""} onChange={(e) => setForm({...form, agency_name: e.target.value})} placeholder="opcional" />
              </Field>
            </div>
            <Field label="Anotações">
              <Textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({...form, notes: e.target.value})} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button data-testid="save-ag-btn" onClick={save} className="bg-[#1B3A2F] hover:bg-[#244A3C]">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AgCard({ item, onClick, onMove, onDelete, waLink }) {
  return (
    <Card
      data-testid={`ag-card-${item.id}`}
      className={`p-3 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition border ${item.urgent ? 'border-red-300 bg-red-50' : 'border-stone-200 bg-white'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0" onClick={onClick}>
          <div className="font-semibold text-sm truncate">{item.title || item.address || "Sem título"}</div>
          {item.address && item.title !== item.address && (
            <div className="text-xs text-stone-500 truncate">{item.address}</div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-stone-600 mt-1.5">
            <Phone className="w-3 h-3" /> {item.phone}
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="outline" className="text-[10px] capitalize">{item.listing_type}</Badge>
            <span className="text-[10px] text-stone-500 inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {item.days_in_stage}d na etapa
            </span>
            {item.urgent && (
              <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] hover:bg-red-100">
                <AlertCircle className="w-3 h-3 mr-0.5" /> Urgente
              </Badge>
            )}
          </div>
          {waLink && item.stage !== "agenciado" && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ag-wa-link-${item.id}`}
              onClick={(e) => e.stopPropagation()}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#D4651A] hover:underline"
            >
              <MessageCircle className="w-3 h-3" /> Abrir WhatsApp com copy
            </a>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`ag-menu-${item.id}`} onClick={(e) => e.stopPropagation()} className="p-1 text-stone-400 hover:text-stone-700">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <div className="px-2 py-1 text-xs text-stone-500">Mover para:</div>
            {STAGES.filter(s => s.id !== item.stage).map(s => (
              <DropdownMenuItem key={s.id} onClick={() => onMove(item, s.id)}>
                <ArrowRight className="w-3.5 h-3.5 mr-2" /> {s.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem onClick={() => onDelete(item.id)} className="text-red-600">
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-stone-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
